import { FC, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { MplusStatuses } from 'widgets/mplus/mplus-payment/module'

import { CheckPromoCodeResponse } from 'entities/promo-code/model'
import { PromoButton } from 'entities/promo-code/ui/promo-button'
import { CertificateUse } from 'entities/sales/components/certificate-use'
import { ConfirmPayment } from 'entities/sales/components/confirm-payment'
import userStore from 'entities/user/module/user-store'

import { MyTicketItem } from 'shared/api/MyTicketsModel'
import { PriceCertificate } from 'shared/constants'
import { priceShow } from 'shared/lib/priceShow'
import { TicketItem } from 'shared/models/ticket'
import { Button } from 'shared/ui/button'
import { CheckBox } from 'shared/ui/check-box'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { MbonusLineInfo } from 'shared/ui/mbonus/mbonus-line-info'
import { PopupContent } from 'shared/ui/poup-content'
import { Skeleton } from 'shared/ui/skeleton'

import { PaymentType } from '../../model'

import { ConnectMplusCard } from './ui/connect-mplus-card'
import { DisabledMplusOption } from './ui/disabled-mplus-option'
import { MplusDetails } from './ui/mplus-details'
import { MplusRequirements } from './ui/mplus-requirements'
import { PaymentIcon } from './ui/payment-icon'
import { PaymentOption } from './ui/payment-option'
import { StatusNotice } from './ui/status-notice'

import { getAvailableMethods } from './lib/get-available-methods'
import { useMplusStatus } from './lib/use-mplus-status'
import s from './styles.module.scss'

interface Props {
  finalPrice: number
  isLoading?: boolean
  isPromoAvailable?: boolean
  isMplusAvailable?: boolean
  isMplusEnabled?: boolean
  countPassengerInfo: number
  selectedTicket: TicketItem | MyTicketItem
  promoCode?: CheckPromoCodeResponse
  onUsePromoCode?: () => void
  onOpenMplus?: () => void
  onRegularPay?: (isCertificate: boolean) => void
  onMplusPay?: (dayInMonth: number) => void
  onMInstalmentPayment?: () => void
}

export const PaymentMethod: FC<Props> = observer(
  ({
    finalPrice,
    isLoading,
    isPromoAvailable,
    isMplusAvailable,
    isMplusEnabled = true,
    countPassengerInfo,
    selectedTicket,
    promoCode,
    onUsePromoCode,
    onOpenMplus,
    onRegularPay,
    onMplusPay,
    onMInstalmentPayment,
  }) => {
    const { t } = useTranslation()

    const { openDialog, closeDialog } = useDialogBottomSheet()

    const [selectedMethod, setSelectedMethod] = useState<PaymentType>('regular')
    const [mplusDay, setMplusDay] = useState<number>()
    const [isConfirmed, setIsConfirmed] = useState(false)
    const [isCertificateActive, setIsCertificateActive] = useState(false)

    const currentPrice = finalPrice - (Number(promoCode?.discount_amount) || 0)

    const mplus = useMplusStatus({
      price: currentPrice,
      tranchePrice: selectedTicket?.tranche_amount,
      countPassengers: countPassengerInfo,
      isMplusAvailable: isMplusEnabled && isMplusAvailable,
    })

    const isMplusLoading = isMplusEnabled && mplus.isLoading

    const isMplusAllowed = mplus.status === MplusStatuses.ALLOW.key

    // Credit line is active: M+ can be selected (even if the limit is exceeded)
    const isMplusSelectable =
      isMplusEnabled &&
      !mplus.isLoading &&
      (isMplusAllowed || mplus.status === MplusStatuses.NOT_ENOUGH_CASH.key)

    // No credit line: offer to connect M+
    const isMplusConnectable =
      isMplusEnabled &&
      !mplus.isLoading &&
      (mplus.status === MplusStatuses.INCLUDE.key ||
        mplus.status === MplusStatuses.NOT_ALLOW_MPLUS.key)

    // Order is cheaper than the M+ minimum
    const isMplusPriceTooLow =
      isMplusEnabled &&
      !mplus.isLoading &&
      mplus.status === MplusStatuses.NOT_ALLOW_PRICE.key

    const availableMethods = getAvailableMethods({
      price: currentPrice,
      isMplusSelectable,
      isMInstalmentAvailable: userStore.userSettings?.is_minstallment_available,
    })

    const activeMethod = availableMethods.includes(selectedMethod)
      ? selectedMethod
      : 'regular'

    // Certificate is for the regular payment only and can't be combined
    // with a promo code
    const isCertificateAvailable =
      activeMethod === 'regular' &&
      !promoCode &&
      Boolean(userStore.getAccountInfo()?.has_certificate)

    const isCertificateUsed = isCertificateAvailable && isCertificateActive

    const payablePrice = isCertificateUsed
      ? Math.max(finalPrice - PriceCertificate, 0)
      : currentPrice

    const isPayDisabled =
      !isConfirmed ||
      (activeMethod === 'mplus' && (!isMplusAllowed || !mplusDay))

    const onPay = () => {
      switch (activeMethod) {
        case 'mplus':
          if (mplusDay) onMplusPay?.(mplusDay)
          break
        case 'mInstalment':
          onMInstalmentPayment?.()
          break
        default:
          onRegularPay?.(isCertificateUsed)
      }
    }

    const onConnectMplus = () => {
      if (mplus.status === MplusStatuses.INCLUDE.key) {
        onOpenMplus?.()
        return
      }

      openDialog({
        isDialog: false,
        child: (
          <PopupContent
            padding={[6]}
            button={t('ok')}
            title={t('mplus.connect_mplus')}
            content={<MplusRequirements />}
            onClick={closeDialog}
          />
        ),
      })
    }

    return (
      <div className={s.container}>
        <p className={clsx(s.title, 'semi-bold-18')}>
          {t('mplus.payment_type')}
        </p>

        <div className={s.options}>
          <PaymentOption
            icon={<PaymentIcon variant="mbank" />}
            title={t('mplus.pay_current')}
            subTitle={t('payment_method.debit_from_mbank')}
            isActive={activeMethod === 'regular'}
            onClick={() => setSelectedMethod('regular')}
          />

          {isMplusLoading && (
            <Skeleton
              height={56}
              borderRadius={16}
            />
          )}

          {isMplusSelectable && (
            <PaymentOption
              icon={<PaymentIcon variant="mplusActive" />}
              title={t('mplus.arrange')}
              subTitle={t('payment_method.now_free')}
              isActive={activeMethod === 'mplus'}
              onClick={() => setSelectedMethod('mplus')}
            />
          )}

          {isMplusConnectable && <ConnectMplusCard onClick={onConnectMplus} />}

          {isMplusPriceTooLow && <DisabledMplusOption />}

          {availableMethods.includes('mInstalment') && (
            <PaymentOption
              icon={<PaymentIcon variant="minstallment" />}
              title={t('minstallment.info_title')}
              subTitle={t('payment_method.now_free')}
              isActive={activeMethod === 'mInstalment'}
              onClick={() => setSelectedMethod('mInstalment')}
            />
          )}
        </div>

        {activeMethod === 'mplus' && (
          <div className={s.details}>
            <MplusDetails
              hasLimit={isMplusAllowed}
              limit={mplus.creditInfo?.available_amount}
              countPassengers={countPassengerInfo}
              calculatedInfo={mplus.calculatedInfo}
              paymentDay={mplusDay}
              onChangePaymentDay={setMplusDay}
            />
          </div>
        )}

        {activeMethod === 'mInstalment' && (
          <div className={s.details}>
            <StatusNotice
              title={t('payment_method.installment_notice_title')}
              description={t('payment_method.installment_notice_description')}
            />
          </div>
        )}

        {activeMethod === 'regular' && (
          <div className={s.details}>
            {isCertificateAvailable && (
              <CertificateUse
                isActive={isCertificateActive}
                setIsActive={setIsCertificateActive}
              />
            )}

            {!isCertificateUsed && (
              <PromoButton
                isLoading={isLoading}
                onClick={onUsePromoCode}
                isAvailable={isPromoAvailable}
                isPromoActive={Boolean(promoCode)}
                discountText={t('promocode_amount', {
                  price: priceShow(promoCode?.discount_amount),
                })}
              />
            )}
          </div>
        )}

        {parseInt(selectedTicket?.bonus_amount || '0') > 0 && (
          <div className={s.bonusInformation}>
            <MbonusLineInfo
              isTransparent
              bonus={selectedTicket?.bonus_amount}
            />
          </div>
        )}

        <div className={s.summary}>
          <p className={clsx(s.passengers, 'regular-16')}>
            {/* {t('purchase_page.price_for_passengers', {
              count: countPassengerInfo,
            })} */}
            {t('total')}
          </p>

          <div className={clsx(s.price, 'bold-16')}>
            {payablePrice !== finalPrice && (
              <p className={s.oldPrice}>
                {priceShow(String(finalPrice))} &#8384;
              </p>
            )}

            <p>
              {payablePrice > 0 ? (
                <>{priceShow(String(payablePrice))} &#8384;</>
              ) : (
                t('free')
              )}
            </p>
          </div>
        </div>

        <div className={s.confirm}>
          <CheckBox
            isBigSize
            isChecked={isConfirmed}
            onClick={(value) => setIsConfirmed(Boolean(value))}
          />

          <ConfirmPayment selectedTicket={selectedTicket} />
        </div>

        <Button
          loading={isLoading}
          disabled={isPayDisabled}
          onClick={onPay}
        >
          <span className="semi-bold-16">{t('to_pay')}</span>
        </Button>
      </div>
    )
  },
)
